import { Banner, BlockStack, Card, Select, Text } from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { apiFetch } from "../api/client";
import { t } from "../lib/i18n";

/**
 * 実績が少ない商品の日販をどちら側へ倒すか（店単位の方針）。
 *
 * 🔴 **統計ではなく資金繰りの判断**＝実績が数日しか無い商品の日販は「分からない」。
 * 教科書どおりなら不確実性が高いほど多めに持つのが正解（欠品確率を一定に保つため）
 * だが、開店直後の店では過剰在庫のほうが致命的で、欠品は売上機会を失うだけでも
 * 在庫は現金を止める。どちらが正しいかは店の資金繰り次第なので選ばせる
 * （2026-08-04 ユーザー決定）。真実源＝backend `services/demand_stance.py`。
 *
 * 🔑 **%を出さない**＝数字を出すと「95%のほうが良さそう」と読まれるが、実際は
 * 良し悪しではなく方向の選択。順位があるように見せない。「信頼度」「サービス
 * レベル」等の統計語も出さない（ユーザー指摘 2026-08-04）。
 */

/** 発注点一覧のキャッシュ。方針を変えると全行の数字が変わるので引き直す。 */
const POLICIES_QUERY_KEY = ["variant-policies"];
const ME_QUERY_KEY = ["me-demand-stance"];

type Stance = "conservative" | "observed" | "generous";

function stanceOptions() {
  return [
    {
      label: t("少なめに見る — 在庫を抱えたくない"),
      value: "conservative" satisfies Stance,
    },
    { label: t("実績どおり — 直近の売れ行きをそのまま使う"), value: "observed" satisfies Stance },
    { label: t("多めに見る — 欠品を出したくない"), value: "generous" satisfies Stance },
  ];
}

export function DemandStanceCard() {
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState(false);

  const meQuery = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => apiFetch<{ demand_stance?: string }>("/api/me"),
  });

  const mutation = useMutation({
    mutationFn: (stance: Stance) =>
      apiFetch("/api/me/demand-stance", {
        method: "PATCH",
        body: { demand_stance: stance },
      }),
    onMutate: () => setSaveError(false),
    onSuccess: () => {
      setSaveError(false);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      // 🔴 発注点は方針から計算されるので、保存したら一覧を引き直す。
      //    引き直さないと「保存したのに数字が変わらない」に見える。
      void queryClient.invalidateQueries({ queryKey: POLICIES_QUERY_KEY });
    },
    // 🔴 失敗を黙って飲まない＝押した本人は変わったと思っている。
    onError: () => setSaveError(true),
  });

  // 取得できていない間は既定を表示（Select が空で点滅しないように）。
  const current = (meQuery.data?.demand_stance ?? "observed") as Stance;

  return (
    <Card>
      <BlockStack gap="200">
        {meQuery.isError ? (
          <Banner
            tone="critical"
            title={t("現在の設定を取得できませんでした")}
            action={{
              content: t("再試行"),
              onAction: () => void meQuery.refetch(),
            }}
          >
            <Text as="p">
              {t("現在値を確認できないため、誤って上書きしないよう変更操作を停止しています。")}
            </Text>
          </Banner>
        ) : (
          <Select
            label={t("売れ行きの実績が少ない商品の見積もり")}
            options={stanceOptions()}
            value={current}
            onChange={(value) => mutation.mutate(value as Stance)}
            disabled={meQuery.isLoading || mutation.isPending}
          />
        )}
        {/* 🔑 「いつ効いて、いつ効かなくなるか」を言う＝選んだあと放置しても
            害が無いことが伝わる。実績がたまると差が自然に縮む（標準誤差が
            サンプル数で決まるため）。 */}
        <Text as="p" variant="bodySm" tone="subdued">
          {t(
            "実績が数日しかない商品ほど、この設定の影響が大きくなります。30日分たまると、どの設定でもほぼ同じ数字になります。",
          )}
        </Text>
        {saveError && (
          <Banner tone="critical" title={t("設定の保存結果を確認できませんでした")}>
            <Text as="p">
              {t("通信状況によっては保存済みの可能性があります。最新の設定を再取得しています。表示を確認してから、必要な場合だけ選び直してください。")}
            </Text>
          </Banner>
        )}
      </BlockStack>
    </Card>
  );
}
